import { useCallback, useMemo, useState } from 'react';
import api from '../api/client';
import MarkdownText from './MarkdownText';
import TagTokenInput from './TagTokenInput';

const PAGE_SIZE = 500;

const FILTER_OPTIONS = [
  { value: 'all', label: 'Все по фильтру' },
  { value: 'success_any', label: 'Успешно решённые' },
  { value: 'no_attempts', label: 'Без попыток' },
  { value: 'no_success_in_months', label: 'Нет успеха за выбранный период' },
  { value: 'attempted_never_succeeded', label: 'Пробовали, но ни одной успешной попытки' },
];

const MONTH_OPTIONS = [1, 2, 3, 5];

function formatTaskNumber(task) {
  const n = task?.source_number;
  if (n != null && String(n).trim() !== '') return String(n);
  if (task?.slug) return String(task.slug);
  if (task?.id != null) return `#${task.id}`;
  return '—';
}

function taskTitle(task) {
  const t = typeof task?.title === 'string' ? task.title.trim() : '';
  return t;
}

function statementRaw(task) {
  if (typeof task?.statement === 'string' && task.statement.trim()) return task.statement;
  return '';
}

function taskMatchesListQuery(task, q) {
  if (!q) return true;
  const hay = [
    taskTitle(task),
    statementRaw(task),
    formatTaskNumber(task),
    typeof task?.slug === 'string' ? task.slug : '',
    typeof task?.source_name === 'string' ? task.source_name : '',
    typeof task?.source_number === 'string' ? task.source_number : '',
  ]
    .join('\n')
    .toLowerCase();
  return hay.includes(q);
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

/**
 * Каталог задач для вкладки «Подготовка»: поиск, теги, фильтр прогресса,
 * карточки как в совместном кодировании, кнопки «В занятие» / «В ДЗ».
 * Набор — карта ЕГЭ (снятые и «не ЕГЭ» не показываются).
 */
export default function LessonPrepTaskPicker({
  studentId,
  lessonTaskIds = [],
  homeworkTaskIds = [],
  canAdd = false,
  onAdd,
  adding = false,
}) {
  const [search, setSearch] = useState('');
  const [listFilter, setListFilter] = useState('');
  const [tags, setTags] = useState([]);
  const [progressFilter, setProgressFilter] = useState('all');
  const [months, setMonths] = useState(1);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const lessonSet = useMemo(() => new Set(lessonTaskIds), [lessonTaskIds]);
  const homeworkSet = useMemo(() => new Set(homeworkTaskIds), [homeworkTaskIds]);

  const listFilterNorm = listFilter.trim().toLowerCase();
  const visibleTasks = useMemo(
    () => (tasks || []).filter((t) => taskMatchesListQuery(t, listFilterNorm)),
    [tasks, listFilterNorm],
  );

  const loadPage = useCallback(
    async (nextPage, append) => {
      const sid = Number(studentId);
      if (!sid || Number.isNaN(sid)) return;

      const searchTrimmed = search.trim();
      const tagSlugs = (tags || []).map((t) => t?.slug).filter(Boolean);
      if (!searchTrimmed && tagSlugs.length === 0) {
        setError('Укажите поиск по тексту и/или хотя бы один тег.');
        setHasSubmitted(true);
        return;
      }

      setLoading(true);
      setError('');
      setHasSubmitted(true);
      try {
        const params = new URLSearchParams();
        params.set('filter', progressFilter);
        if (searchTrimmed) params.set('search', searchTrimmed);
        for (const slug of tagSlugs) {
          params.append('tag', slug);
        }
        if (progressFilter === 'no_success_in_months') {
          params.set('months', String(months));
        }
        params.set('page', String(nextPage));
        params.set('page_size', String(PAGE_SIZE));

        const res = await api.get(`/teacher/students/${sid}/tasks-by-tag/?${params.toString()}`);
        const newRows = Array.isArray(res.data?.results) ? res.data.results : [];
        setTasks((prev) => (append ? [...prev, ...newRows] : newRows));
        setTotalCount(typeof res.data?.count === 'number' ? res.data.count : null);
        setHasMore(Boolean(res.data?.next));
        setPage(nextPage);
        if (!append) {
          setExpandedIds(new Set());
          setListFilter('');
        }
      } catch (e) {
        const msg = e?.response?.data?.detail;
        setError(typeof msg === 'string' ? msg : 'Не удалось загрузить задачи.');
        if (!append) {
          setTasks([]);
          setTotalCount(null);
          setListFilter('');
        }
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [studentId, search, tags, progressFilter, months],
  );

  function handleShow(e) {
    e?.preventDefault?.();
    void loadPage(1, false);
  }

  function handleLoadMore() {
    if (!hasMore || loading) return;
    void loadPage(page + 1, true);
  }

  function toggleExpanded(taskId) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }

  function expandAll() {
    setExpandedIds(new Set((visibleTasks || []).map((t) => t.id)));
  }

  function collapseAll() {
    setExpandedIds(new Set());
  }

  const listFilterActive = Boolean(listFilterNorm);
  const emptyAfterListFilter =
    !loading && hasSubmitted && !error && tasks.length > 0 && visibleTasks.length === 0;

  return (
    <div className="lesson-prep-task-picker">
      <form className="lesson-prep-filters" onSubmit={handleShow}>
        <div className="form-group lesson-prep-search-field">
          <label htmlFor="lesson-prep-search" className="visually-hidden">
            Поиск по каталогу
          </label>
          <input
            id="lesson-prep-search"
            type="text"
            className="form-input"
            placeholder="По каталогу: условие, название, номер…"
            title="Подстрока в условии, названии, slug и номере. Вместе с тегами и фильтром — по кнопке «Показать»."
            aria-label="Поиск по каталогу: условие, название, номер"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="lesson-prep-tags-field">
          <TagTokenInput
            label="Теги"
            value={tags}
            onChange={setTags}
            placeholder="Теги: тема, подборка…"
            showTypeFilter
            compact
          />
        </div>

        <div className="form-group lesson-prep-progress-field">
          <label htmlFor="lesson-prep-progress" className="visually-hidden">Прогресс ученика</label>
          <select
            id="lesson-prep-progress"
            className="form-select"
            aria-label="Прогресс ученика"
            value={progressFilter}
            onChange={(e) => setProgressFilter(e.target.value)}
          >
            {FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {progressFilter === 'no_success_in_months' && (
          <div className="form-group lesson-prep-months-field">
            <label htmlFor="lesson-prep-months" className="visually-hidden">Период без успеха</label>
            <select
              id="lesson-prep-months"
              className="form-select"
              aria-label="Период без успеха"
              value={months}
              onChange={(e) => setMonths(Number(e.target.value))}
            >
              {MONTH_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {m} {m === 1 ? 'месяц' : m < 5 ? 'месяца' : 'месяцев'}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="lesson-prep-filters-actions">
          <button type="submit" className="btn btn-sm btn-outline" disabled={loading}>
            {loading && page === 1 ? 'Загрузка…' : 'Показать'}
          </button>
        </div>
      </form>

      {hasSubmitted && (
        <div className="lesson-prep-list-toolbar">
          <div className="lesson-prep-list-toolbar-left">
            <button
              type="button"
              className="btn btn-xs btn-outline"
              onClick={expandAll}
              disabled={visibleTasks.length === 0}
            >
              Развернуть все
            </button>
            <button
              type="button"
              className="btn btn-xs btn-outline"
              onClick={collapseAll}
              disabled={visibleTasks.length === 0}
            >
              Свернуть все
            </button>
            {tasks.length > 0 && (
              <div className="form-group lesson-prep-list-filter-field">
                <label htmlFor="lesson-prep-list-filter" className="visually-hidden">
                  Среди загруженных
                </label>
                <input
                  id="lesson-prep-list-filter"
                  type="search"
                  className="form-input"
                  placeholder="Среди загруженных…"
                  title="Мгновенно сужает уже показанный список без нового запроса"
                  aria-label="Поиск среди загруженных задач"
                  value={listFilter}
                  onChange={(e) => setListFilter(e.target.value)}
                />
              </div>
            )}
          </div>
          {totalCount != null && (
            <span className="text-secondary lesson-prep-count">
              {listFilterActive
                ? `Показано: ${visibleTasks.length} из загруженных ${tasks.length}`
                : `Найдено: ${totalCount}`}
            </span>
          )}
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {!canAdd && (
        <p className="text-secondary">Начните урок, чтобы добавлять задачи в списки.</p>
      )}
      {loading && hasSubmitted && tasks.length === 0 && (
        <p className="text-secondary">Загрузка задач…</p>
      )}
      {!loading && hasSubmitted && !error && tasks.length === 0 && (
        <p className="text-secondary">Ничего не найдено. Измените поиск или тег.</p>
      )}
      {emptyAfterListFilter && (
        <p className="text-secondary">Среди загруженных ничего не подходит. Измените строку фильтра.</p>
      )}

      <div className="lesson-prep-task-scroll">
        <div className="collab-all-tasks-list lesson-prep-task-list">
          {visibleTasks.map((task) => {
            const inLesson = lessonSet.has(task.id);
            const inHomework = homeworkSet.has(task.id);
            const isAdded = inLesson || inHomework;
            const isExpanded = expandedIds.has(task.id);
            const statement = statementRaw(task);
            const snippet = statement ? String(statement).slice(0, 200) : '';
            const canExpand = statement && String(statement).length > 200;
            const allTags = Array.isArray(task?.tags) ? task.tags : [];
            const visibleTags = allTags.slice(0, 4);
            const hiddenTagCount = Math.max(0, allTags.length - visibleTags.length);

            return (
              <div
                key={task.id}
                className={`collab-all-task-card ${isAdded ? 'is-added' : ''}`}
              >
                <div className="collab-all-task-main">
                  <div className="collab-all-task-head-row">
                    <div className="collab-all-task-head-left">
                      <span className="collab-all-task-number">{formatTaskNumber(task)}</span>
                      {visibleTags.length > 0 && (
                        <span className="collab-all-task-inline-tags" aria-label="Теги задачи">
                          {visibleTags.map((tag) => (
                            <span key={tag.id ?? tag.slug ?? tag.name} className="tag tag-compact" title={tag.name}>
                              {tag.name}
                            </span>
                          ))}
                          {hiddenTagCount > 0 && (
                            <span className="tag tag-compact tag-compact-more" title={`Ещё тегов: ${hiddenTagCount}`}>
                              +{hiddenTagCount}
                            </span>
                          )}
                        </span>
                      )}
                      {taskTitle(task) ? (
                        <span className="collab-all-task-name">{taskTitle(task)}</span>
                      ) : null}
                    </div>
                    <div className="collab-all-task-head-right">
                      {canAdd && (
                        <>
                          <button
                            type="button"
                            className="btn btn-xs btn-outline"
                            disabled={adding || inLesson}
                            onClick={() => onAdd?.('lesson', task.id)}
                          >
                            {inLesson ? 'В занятии' : 'В занятие'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-xs btn-outline"
                            disabled={adding || inHomework}
                            onClick={() => onAdd?.('homework', task.id)}
                          >
                            {inHomework ? 'Уже в ДЗ' : 'В ДЗ'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {statement ? (
                    <div className="collab-all-task-statement">
                      {!isExpanded ? (
                        <>
                          <div className="collab-all-task-statement-snippet">
                            {snippet}{canExpand ? '…' : ''}
                          </div>
                          {canExpand && (
                            <button
                              type="button"
                              className="btn btn-link btn-xs collab-all-task-statement-toggle"
                              onClick={() => toggleExpanded(task.id)}
                            >
                              Показать полностью
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          <MarkdownText text={statement} className="collab-all-task-statement-md" />
                          <button
                            type="button"
                            className="btn btn-link btn-xs collab-all-task-statement-toggle"
                            onClick={() => toggleExpanded(task.id)}
                          >
                            Свернуть
                          </button>
                        </>
                      )}
                    </div>
                  ) : null}

                  <div className="text-secondary lesson-prep-task-meta">
                    Успех: {fmtDate(task.last_success_at)} · Любая попытка: {fmtDate(task.last_any_attempt_at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {hasSubmitted && tasks.length > 0 && (
          <div className="lesson-prep-load-more">
            <div className="text-secondary" style={{ fontSize: '0.8rem', marginBottom: '0.35rem' }}>
              {listFilterActive
                ? `Показано: ${visibleTasks.length} из загруженных ${tasks.length}`
                : (
                  <>
                    Загружено: {tasks.length}
                    {typeof totalCount === 'number' ? ` из ${totalCount}` : ''}
                  </>
                )}
            </div>
            {hasMore ? (
              <button
                type="button"
                className="btn btn-outline btn-xs"
                disabled={loading}
                onClick={handleLoadMore}
              >
                {loading ? 'Загрузка…' : 'Загрузить ещё'}
              </button>
            ) : (
              <div className="text-secondary" style={{ fontSize: '0.8rem' }}>
                Это все задачи по текущему фильтру.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
